import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { RevealOnScroll } from "@/components/ui-custom/RevealOnScroll";
import { SectionHeading } from "@/components/ui-custom/SectionHeading";

// Real coordinates of each division's main town, for the Leaflet map markers
const LAYOUT = [
  { key: "Miri", lat: 4.3993, lng: 113.9915 },
  { key: "Bintulu", lat: 3.1667, lng: 113.0333 },
  { key: "Sibu", lat: 2.2870, lng: 111.8305 },
  { key: "Sri Aman", lat: 1.2373, lng: 111.4633 },
  { key: "Samarahan", lat: 1.4550, lng: 110.5949 },
  { key: "Kuching", lat: 1.5533, lng: 110.3592 },
];

function buildDivisionIcon(count, isActive) {
  const size = isActive ? 48 : 36;
  return L.divIcon({
    className: "",
    html: `<div style="width:${size}px;height:${size}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;font-family:'Manrope',sans-serif;font-weight:800;font-size:${isActive ? 15 : 12}px;color:${isActive ? "#111111" : "#FFFFFF"};background:${isActive ? "#F4C430" : "rgba(17,17,17,0.85)"};border:2px solid ${isActive ? "#F4C430" : "rgba(244,196,48,0.55)"};box-shadow:0 0 0 5px ${isActive ? "rgba(244,196,48,0.18)" : "rgba(244,196,48,0.08)"};transition:all .2s ease;">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

export function DivisionMap({ byDaerah }) {
  const [active, setActive] = useState(null);
  const max = Math.max(1, ...Object.values(byDaerah || {}));

  return (
    <section className="bg-piks-black text-white py-20 sm:py-28 relative overflow-hidden" data-testid="division-map-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <RevealOnScroll>
          <SectionHeading number="05" title="Sebaran Ahli" light />
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3">Komuniti Bodyguard Sarawak</h2>
          <p className="text-white/50 max-w-lg mb-12 text-sm sm:text-base">
            Ahli PPPS tersebar di seluruh bahagian utama Sarawak. Sentuh / tunjuk setiap daerah pada peta untuk lihat jumlah ahli.
          </p>
        </RevealOnScroll>

        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-5 sm:gap-6 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            data-testid="division-map-leaflet"
            className="division-map-wrap relative rounded-2xl border border-piks-gold/25 overflow-hidden h-[340px] sm:h-[420px] lg:h-[480px] shadow-2xl shadow-black/40"
          >
            <MapContainer
              center={[2.55, 112.6]}
              zoom={6}
              minZoom={5}
              maxZoom={10}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%", background: "#0d0d0d" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              {LAYOUT.map((d) => {
                const count = byDaerah?.[d.key] ?? 0;
                const isActive = active === d.key;
                return (
                  <Marker
                    key={d.key}
                    position={[d.lat, d.lng]}
                    icon={buildDivisionIcon(count, isActive)}
                    eventHandlers={{
                      mouseover: () => setActive(d.key),
                      mouseout: () => setActive(null),
                      click: () => setActive(isActive ? null : d.key),
                    }}
                  >
                    <Popup>
                      <span className="font-semibold">{d.key}</span> — {count} ahli
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-3 sm:gap-4">
            {LAYOUT.map((d, i) => {
              const count = byDaerah?.[d.key] ?? 0;
              const pct = Math.round((count / max) * 100);
              const isActive = active === d.key;
              return (
                <motion.button
                  key={d.key}
                  type="button"
                  data-testid={`division-card-${d.key.toLowerCase().replace(/\s/g, "-")}`}
                  onMouseEnter={() => setActive(d.key)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() => setActive(isActive ? null : d.key)}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`relative rounded-xl border p-4 text-left transition-colors duration-300 ${
                    isActive ? "bg-piks-red/15 border-piks-red" : "bg-white/5 border-white/10 hover:border-piks-gold/50"
                  }`}
                >
                  <MapPin className={`w-4 h-4 mb-2 ${isActive ? "text-piks-gold" : "text-white/40"}`} />
                  <p className="text-xs sm:text-sm font-semibold">{d.key}</p>
                  <p className={`font-display text-lg sm:text-xl font-extrabold ${isActive ? "text-piks-gold" : "text-white"}`}>{count}</p>
                  <div className="mt-2 h-[3px] w-full bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-piks-red to-piks-gold"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${pct}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 + i * 0.06 }}
                    />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
